/**
 * UAR Selectors for accessing UAR state (cart, queue, processed requests)
 */

/**
 * Select all UAR cart items
 */
export const selectCartItems = state => {
	return state.uar?.actionCart?.items || [];
};

/**
 * Select UAR cart item count
 */
export const selectCartItemCount = state => {
	return selectCartItems(state).length;
};

/**
 * Select action for a specific staff member and app
 */
export const selectActionForApp = (state, staffMemberId, appId) => {
	const items = selectCartItems(state);
	const item = items.find(
		cartItem =>
			String(cartItem.staffMemberId) === String(staffMemberId) &&
			String(cartItem.appId) === String(appId)
	);
	return item?.action ?? null;
};

/**
 * Select all UAR cart items grouped by staff member
 */
export const selectCartByStaffMember = (state, staffMemberId) => {
	const items = selectCartItems(state);
	return items.filter(item => String(item.staffMemberId) === String(staffMemberId));
};

/**
 * Select entire UAR queue
 */
export const selectModifyQueue = state => {
	return state.uar?.modifyQueue?.items || [];
};

/**
 * Select current UAR queue index
 */
export const selectQueueCurrentIndex = state => {
	return state.uar?.modifyQueue?.currentIndex ?? 0;
};

/**
 * Select the current UAR queue item
 */
export const selectCurrentQueueItem = state => {
	const queue = selectModifyQueue(state);
	const currentIndex = selectQueueCurrentIndex(state);
	return queue[currentIndex] ?? null;
};

/**
 * Select the next UAR queue item (after current)
 */
export const selectNextQueueItem = state => {
	const queue = selectModifyQueue(state);
	const currentIndex = selectQueueCurrentIndex(state);
	const nextIndex = currentIndex + 1;
	return queue[nextIndex] ?? null;
};

/**
 * Select UAR queue progress info
 */
export const selectQueueProgress = state => {
	const queue = selectModifyQueue(state);
	const currentIndex = selectQueueCurrentIndex(state);
	return {
		current: currentIndex + 1,
		total: queue.length,
	};
};

/**
 * Check if there are more items in the UAR queue
 */
export const selectHasMoreQueueItems = state => {
	const queue = selectModifyQueue(state);
	const currentIndex = selectQueueCurrentIndex(state);
	return currentIndex + 1 < queue.length;
};

/**
 * Select all UAR processed requests
 */
export const selectProcessedRequests = state => {
	return state.uar?.processedRequests?.items || [];
};

/**
 * Check if UAR queue is empty
 */
export const selectIsQueueEmpty = state => {
	return selectModifyQueue(state).length === 0;
};

/**
 * Check if in annual review flow (has active UAR queue or processed requests)
 */
export const selectIsAnnualReviewFlowActive = state => {
	return selectModifyQueue(state).length > 0 || selectProcessedRequests(state).length > 0;
};
